import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { expandGambiaPhone, normalizePhone } from './policyCatalog';

export const PULSE_WAVE_MSISDN = '2207959000';
export const PULSE_WAVE_DISPLAY = '+220 795 9000';
export const PULSE_PAY_MSISDN = '2207959000';
export const PULSE_PAY_DISPLAY = '+220 795 9000';
export const DEFAULT_BROKER_COMMISSION = 0.10;

export function defaultWallets() {
    return {
        pulsePayNumber: PULSE_PAY_DISPLAY,
        waveNumber: PULSE_WAVE_DISPLAY,
        orangeNumber: '',
        qmoneyNumber: '',
        commissionRateBroker: DEFAULT_BROKER_COMMISSION
    };
}

export function walletsFromTenant(data) {
    const w = (data && data.wallets) || {};
    return {
        pulsePayNumber: w.pulsePayNumber || defaultWallets().pulsePayNumber,
        waveNumber: w.waveNumber || defaultWallets().waveNumber,
        orangeNumber: w.orangeNumber || '',
        qmoneyNumber: w.qmoneyNumber || '',
        commissionRateBroker: Number(w.commissionRateBroker) > 0 ? Number(w.commissionRateBroker) : DEFAULT_BROKER_COMMISSION
    };
}

export function useTenantWallets(tenantId) {
    const [wallets, setWallets] = useState(defaultWallets());
    useEffect(() => {
        if (!tenantId) return;
        getDoc(doc(db, 'tenants', tenantId)).then((snap) => {
            if (snap.exists()) setWallets(walletsFromTenant(snap.data()));
        }).catch(() => {});
    }, [tenantId]);
    return wallets;
}

export function normalizeMsisdn(value) {
    let digits = String(value || '').replace(/\D/g, '');
    if (digits.startsWith('00')) digits = digits.slice(2);
    if (digits.startsWith('220')) return digits;
    if (digits.length === 7) digits = '220' + digits;
    if ((digits.length === 8 || digits.length === 9) && digits.startsWith('7')) digits = '220' + digits;
    return digits;
}

export function formatMsisdn(value) {
    const d = normalizeMsisdn(value || PULSE_WAVE_MSISDN);
    if (d.startsWith('220') && d.length === 10) return '+220 ' + d.slice(3, 6) + ' ' + d.slice(6);
    if (d.startsWith('220') && d.length === 12) return '+220 ' + d.slice(3);
    return d ? '+' + d : PULSE_WAVE_DISPLAY;
}

export function walletPayNumber(wallets, method) {
    const w = wallets || defaultWallets();
    const m = String(method || 'cash').toLowerCase().replace(/[\s_-]/g, '');
    if (m === 'cash') return '';
    if (m === 'orange' || m === 'orangemoney') return w.orangeNumber || w.pulsePayNumber || w.waveNumber;
    if (m === 'qmoney') return w.qmoneyNumber || w.pulsePayNumber || w.waveNumber;
    if (m === 'wave') return w.waveNumber || PULSE_WAVE_DISPLAY;
    if (m === 'pulsepay' || m === 'pulse') return w.pulsePayNumber || PULSE_PAY_DISPLAY;
    return w.pulsePayNumber || w.waveNumber || PULSE_PAY_DISPLAY;
}

export function walletBrand(method) {
    const m = String(method || 'cash').toLowerCase().replace(/[\s_-]/g, '');
    if (m === 'orange' || m === 'orangemoney') return 'Orange Money';
    if (m === 'qmoney') return 'QMoney';
    if (m === 'cash') return 'Cash';
    if (m === 'wave') return 'Wave';
    if (m === 'pulsepay' || m === 'pulse') return 'Pulse Pay';
    return 'Cash';
}

export function isWalletMethod(method) {
    const m = String(method || '').toLowerCase().replace(/[\s_-]/g, '');
    return m === 'wave' || m === 'qmoney' || m === 'orange' || m === 'orangemoney' || m === 'pulsepay' || m === 'pulse';
}

export function isPulsePay(method) {
    const m = String(method || '').toLowerCase().replace(/[\s_-]/g, '');
    return m === 'pulsepay' || m === 'pulse';
}

export function normalizePlate(value) {
    return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function toJsDate(value) {
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate();
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

export function daysUntilExpiry(policy) {
    const d = toJsDate(policy && policy.expiryDate);
    if (!d) return null;
    return Math.ceil((d.getTime() - Date.now()) / 86400000);
}

export function splitPremium(policy) {
    const packed = policy && policy.premium;
    if (packed && Number(packed.grossPremium) > 0) {
        return {
            nrp: Number(packed.nrp) || 0,
            wmf: Number(packed.wmf) || 0,
            graTax: Number(packed.graTax) || 0,
            grossPremium: Number(packed.grossPremium) || 0
        };
    }
    const gp = Number((policy && (policy.premiumAmountGMD || policy.premiumAmount)) || 0);
    const nrp = Math.round(gp / 1.15);
    const wmf = Math.round(nrp * 0.10);
    const graTax = Math.max(0, gp - nrp - wmf);
    return { nrp, wmf, graTax, grossPremium: gp };
}

export function whatsappHref(phone, text) {
    const n = expandGambiaPhone(phone) || normalizePhone(phone);
    return 'https://wa.me/' + n + '?text=' + encodeURIComponent(text || '');
}

export function smsHref(phone, text) {
    const n = expandGambiaPhone(phone) || normalizePhone(phone);
    return 'sms:+' + n + '?body=' + encodeURIComponent(text || '');
}

export function certificateMessage({ companyName, name, plate, expiry, url, pin }) {
    const bits = [
        (companyName || 'Pulse Insurance') + ' digital certificate for ' + (name || 'the policyholder') + '.'
    ];
    if (plate) bits.push('Plate ' + plate + '.');
    if (expiry) bits.push('Valid to ' + expiry + '.');
    if (url) bits.push('Police QR: ' + url);
    if (pin) bits.push('Client portal PIN ' + pin + ' at ' + (typeof window !== 'undefined' ? window.location.origin : '') + '/client/login');
    return bits.join(' ');
}

export function renewalMessage({ companyName, name, plate, expiry, amount, wave, payTo, brand, url, cash }) {
    const dest = payTo || wave || PULSE_PAY_DISPLAY;
    const rail = brand || (cash ? 'Cash' : 'Pulse Pay');
    const payBit = cash || String(rail).toLowerCase() === 'cash'
        ? ' Renew by paying D' + Number(amount || 0).toLocaleString() + ' cash at the office.'
        : ' Renew by sending D' + Number(amount || 0).toLocaleString() + ' via ' + rail + ' to ' + dest + '.';
    return (
        (companyName || 'Your insurer') + ': cover for ' + (name || 'you') +
        (plate ? ' (' + plate + ')' : '') +
        ' expires ' + (expiry || 'soon') + '.' + payBit +
        (url ? ' Certificate: ' + url : '')
    );
}

export function wavePayMessage({ amount, wave, policyNumber, name }) {
    return payInstruction({ amount, method: 'wave', number: wave, policyNumber, name });
}

export function pulsePayMessage({ amount, number, policyNumber, name }) {
    return payInstruction({ amount, method: 'pulsepay', number, policyNumber, name });
}

export function payInstruction({ amount, method, number, policyNumber, name, companyName }) {
    const brand = walletBrand(method);
    if (brand === 'Cash' || !isWalletMethod(method)) {
        return (
            'Pay D' + Number(amount || 0).toLocaleString() +
            ' cash at the ' + (companyName || 'office') +
            (policyNumber ? ' for policy ' + policyNumber : '') +
            (name ? ' — ' + name : '') +
            '. Cash at the office is the default payment method.'
        );
    }
    const dest = number || (isPulsePay(method) ? PULSE_PAY_DISPLAY : PULSE_WAVE_DISPLAY);
    const live = isPulsePay(method)
        ? ' Payment is confirmed in Pulse Pay (Pulse IT Group). Staff record the txn ref until a live aggregator is bound.'
        : ' Use the ' + brand + ' confirmation as your reference. This is a pay-to-number rail — not a merchant API.';
    return (
        'Pay D' + Number(amount || 0).toLocaleString() +
        ' via ' + brand + ' to ' + dest +
        (isPulsePay(method) ? ' (Pulse Pay · Pulse IT Group)' : '') +
        (policyNumber ? ' for policy ' + policyNumber : '') +
        (name ? ' — ' + name : '') +
        '.' + live
    );
}

export function formatGmdAmount(n) {
    return 'D' + Number(n || 0).toLocaleString() + '.00';
}
